import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import QRCode from 'qrcode';
import { pdf } from '@react-pdf/renderer';

// MUI
import { Backdrop, Box, LinearProgress, Paper, Tooltip, Typography } from '@mui/material';
import { InfoOutlined, Preview, Print, QrCode2 } from '@mui/icons-material';

// Ui
import AppPage from '@/ui/AppPage/AppPage';
import MapCentricPage from '@/ui/MapCentric/MapCentricPage';
import TableButton from '@/ui/Table/ActionButtons/TableButton';

// Context
import DialogContext from '@/context/DialogContext/DialogContext';
import SnackbarContext, { SnackbarContextType } from '@/context/SnackbarContext/SnackbarContext';
import TenantContext, { TenantContextType } from '@/context/TenantContext/TenantContext';

// Lib
import Permissions from '@/lib/permissions';

// Models
import UpitnikGrobnaMjesta from '@/models/groblja/upitnik_grobna_mjesta';

// Services
import { upitnikService, UpitnikListic } from '@/services/upitnikService';

// Types
import { DCRecord } from '@/@types/lib/dataController';

import UpitnikListiciA7PDF, { UpitnikListiciA7Texts } from './UpitnikListiciA7PDF';

// test print QR, the public page shows its generic error for it
const PROBNI_GID = '00000000-0000-0000-0000-000000000000';
// a slip for these only leads to the "recorded" / redirect / error screen
const SKIP_STATUSES = ['ZATVOREN', 'PREUSMJEREN', 'ISTEKAO'];
const OZNAKA_PARTS = ['odjel', 'polje', 'red', 'broj'] as const;

type Progress = { text: string; value?: number };
type Selection = { [key: string]: DCRecord };

const upitnikUrl = (gid: string) => `${window.location.origin}/groblja/upitnik/${gid}`;

const compareNatural = (a?: string | null, b?: string | null) => (a ?? '').localeCompare(b ?? '', 'hr', { numeric: true });

// walking order, so the printed stack can be handed out row by row
const sortListici = (listici: UpitnikListic[]) =>
  [...listici].sort(
    (a, b) =>
      compareNatural(a.groblje_naziv, b.groblje_naziv) ||
      compareNatural(a.odjel, b.odjel) ||
      compareNatural(a.polje, b.polje) ||
      compareNatural(a.red, b.red) ||
      compareNatural(a.broj, b.broj)
  );

const nextFrame = () =>
  new Promise((resolve) => {
    setTimeout(resolve, 0);
  });

// react-pdf can't embed SVG, the canvas round trip turns it into a small PNG
const toPng = (src: string): Promise<string | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const max = 200;
      const iw = img.naturalWidth || max;
      const ih = img.naturalHeight || max;
      const scale = Math.min(max / iw, max / ih);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(iw * scale));
      canvas.height = Math.max(1, Math.round(ih * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

const recordToListic = (rec: DCRecord): UpitnikListic => ({
  id: rec.id as number,
  groblje_naziv: rec.groblje_naziv as string,
  odjel: rec.odjel as string | null,
  polje: rec.polje as string | null,
  red: rec.red as string | null,
  broj: rec.broj as string | null,
  upitnik_gid: rec.upitnik_gid as string,
  upitnik_status: rec.upitnik_status as UpitnikListic['upitnik_status'],
});

const UpitniciMapCentricPage = () => {
  const { t } = useTranslation();
  const { showConfirmDialog } = useContext(DialogContext);
  const snackbarContext = useContext(SnackbarContext) as SnackbarContextType;
  const tenantContext = useContext(TenantContext) as TenantContextType;

  const [progress, setProgress] = useState<Progress | null>(null);

  const getTexts = (): UpitnikListiciA7Texts => ({
    naslov: t('groblja:upitnik.listic.naslov'),
    opis: t('groblja:upitnik.listic.opis_kratko'),
    oznaka: t('groblja:upitnik.listic.oznaka'),
    komRedar: t('groblja:upitnik.listic.kom_redar'),
    kontakt: t('groblja:upitnik.listic.kontakt'),
    upitnik: t('groblja:upitnik.listic.upitnik'),
    neUklanjati: t('groblja:upitnik.listic.ne_uklanjati'),
    potpis: t('groblja:upitnik.listic.potpis', { datum: moment().format('DD.MM.YYYY.') }),
    url: t('groblja:upitnik.listic.url', { url: `${window.location.host}/groblja/upitnik` }),
    test: t('groblja:upitnik.listic.test'),
  });

  const getGrb = () => {
    const theme = tenantContext?.tenantTheme;
    if (!theme?.crest) return Promise.resolve(null);
    return toPng(`data:${theme.crest_type || 'image/png'};base64,${theme.crest}`);
  };

  const downloadPdf = async (listici: UpitnikListic[], probni: boolean) => {
    const sorted = sortListici(listici);
    const total = sorted.length;

    const qrs: string[] = [];
    await sorted.reduce(async (prev, l, i) => {
      await prev;
      qrs.push(
        await QRCode.toDataURL(upitnikUrl(l.upitnik_gid), {
          errorCorrectionLevel: 'M',
          margin: 1,
          width: 480,
        })
      );
      if (i % 25 === 0) {
        setProgress({ text: t('groblja:upitnici.progress_qr', { done: i, total }), value: (i / total) * 100 });
        await nextFrame();
      }
    }, Promise.resolve());

    setProgress({ text: t('groblja:upitnici.progress_pdf', { count: total }) });
    // let the overlay paint, rendering the PDF blocks the main thread
    await nextFrame();

    const doc = (
      <UpitnikListiciA7PDF
        listici={sorted.map((l, i) => ({
          key: l.id,
          groblje: l.groblje_naziv,
          oznaka: OZNAKA_PARTS.filter((p) => l[p]).map((p) => ({ label: t(`groblja:upitnik.${p}`), value: l[p] as string })),
          qr: qrs[i],
        }))}
        texts={getTexts()}
        grb={await getGrb()}
        probni={probni}
      />
    );
    const blob = await pdf(doc).toBlob();

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `upitnici${probni ? '-probni' : ''}-${moment().format('YYYY-MM-DD-HHmm')}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleIspisi = async (selection: DCRecord[], refresh: () => void) => {
    const toPrint = selection.filter((rec) => !SKIP_STATUSES.includes(rec.upitnik_status as string));
    const skipped = selection.length - toPrint.length;
    const novi = toPrint.filter((rec) => !rec.upitnik_gid).length;

    if (toPrint.length === 0) {
      snackbarContext.showNotification(t('groblja:upitnici.nista_za_ispis'), 'warning');
      return;
    }

    // existing codes never change, so a plain reprint needs no confirmation
    if (novi > 0 || skipped > 0) {
      const confirmResult = await showConfirmDialog({
        title: t('groblja:upitnici.potvrda_naslov') as string,
        text: [
          novi > 0 ? t('groblja:upitnici.potvrda_tekst', { count: novi }) : null,
          skipped > 0 ? t('groblja:upitnici.preskoceno', { count: skipped }) : null,
        ]
          .filter(Boolean)
          .join(' '),
        confirmButtonText: t('groblja:upitnici.potvrdi') as string,
        cancelButtonText: t('buttons.cancel') as string,
      });
      if (!confirmResult.success) return;
    }

    let listici = toPrint.map(recordToListic);

    if (novi > 0) {
      setProgress({ text: t('groblja:upitnici.progress_kodovi') });
      try {
        const resp = await upitnikService.insertKodovi(toPrint.map((rec) => rec.id as number));
        if (!resp.success || !Array.isArray(resp.data)) throw new Error('insertKodovi');
        // fresh statuses from the server, in case a code was closed in the meantime
        listici = (resp.data as UpitnikListic[]).filter((l) => l.upitnik_status === 'AKTIVAN');
      } catch {
        setProgress(null);
        snackbarContext.showNotification(t('groblja:upitnici.greska'), 'error');
        return;
      }
      refresh();
    }

    try {
      await downloadPdf(listici, false);
      snackbarContext.showNotification(t('groblja:upitnici.pdf_gotov', { count: listici.length }), 'success');
    } catch (error) {
      console.error('Error generating PDF:', error);
      snackbarContext.showNotification(
        t(novi > 0 ? 'groblja:upitnici.pdf_greska' : 'groblja:upitnici.ispis_greska'),
        'error',
        novi > 0 ? 'dontAutohide' : undefined
      );
    } finally {
      setProgress(null);
    }
  };

  const handlePrint = async (listici: UpitnikListic[], probni: boolean) => {
    try {
      await downloadPdf(listici, probni);
      snackbarContext.showNotification(t('groblja:upitnici.pdf_gotov', { count: listici.length }), 'success');
    } catch (error) {
      console.error('Error generating PDF:', error);
      snackbarContext.showNotification(t('groblja:upitnici.ispis_greska'), 'error');
    } finally {
      setProgress(null);
    }
  };

  const handleProbniIspis = (selection: DCRecord[]) => {
    const first = sortListici(selection.map(recordToListic))[0];
    handlePrint([{ ...first, upitnik_gid: PROBNI_GID }], true);
  };

  const firstGid = (sel: Selection) => Object.values(sel)[0]?.upitnik_gid as string | undefined;

  const handleOtvori = (sel: Selection) => {
    const gid = firstGid(sel);
    if (gid) window.open(upitnikUrl(gid), '_blank', 'noopener');
  };

  const renderSelectionActions = (refresh: () => void) => [
    <TableButton
      key="otvori"
      variant="outlined"
      allow="one"
      selection={{}}
      isEnabledRecord={(sel: Selection) => !!firstGid(sel)}
      startIcon={<QrCode2 />}
      tooltip={t('groblja:upitnici.otvori_upitnik_hint') as string}
      onClick={(evt, sel: Selection) => handleOtvori(sel)}
    >
      {t('groblja:upitnici.otvori_upitnik')}
    </TableButton>,
    <TableButton
      key="probni"
      variant="outlined"
      allow="many"
      selection={{}}
      startIcon={<Preview />}
      tooltip={t('groblja:upitnici.probni_ispis_hint') as string}
      onClick={(evt, sel: Selection) => handleProbniIspis(Object.values(sel))}
    >
      {t('groblja:upitnici.probni_ispis')}
    </TableButton>,
    <TableButton
      key="ispisi"
      variant="contained"
      allow="many"
      selection={{}}
      startIcon={<Print />}
      tooltip={t('groblja:upitnici.ispisi_hint') as string}
      onClick={(evt, sel: Selection) => handleIspisi(Object.values(sel), refresh)}
    >
      {t('groblja:upitnici.ispisi')}
    </TableButton>,
  ];

  return (
    <AppPage titlett="groblja:titles.upitnici" needPermission={[Permissions.GrobljaUpitnikAdmin]}>
      <MapCentricPage
        model={UpitnikGrobnaMjesta}
        titleToken="groblja:titles.upitnici"
        view="default"
        baseRecordPath=""
        mapId={48}
        mapModel={UpitnikGrobnaMjesta}
        allowExport
        multiSelect
        renderSelectionActions={renderSelectionActions}
        headerControl={
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: { xs: 'none', md: 'block' }, maxWidth: 280, lineHeight: 1.2, textAlign: 'right' }}
            >
              {t('groblja:upitnici.ispis_napomena')}
            </Typography>
            {/* no room for the sentence in a phone toolbar */}
            <Tooltip title={t('groblja:upitnici.ispis_napomena')} enterTouchDelay={0}>
              <InfoOutlined fontSize="small" color="action" sx={{ display: { xs: 'block', md: 'none' } }} />
            </Tooltip>
          </Box>
        }
      />
      <Backdrop open={progress !== null} sx={{ zIndex: 99999 }}>
        <Paper sx={{ p: 3, width: 'min(360px, calc(100vw - 32px))' }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {progress?.text}
          </Typography>
          <LinearProgress variant={progress?.value !== undefined ? 'determinate' : 'indeterminate'} value={progress?.value} />
        </Paper>
      </Backdrop>
    </AppPage>
  );
};

export default UpitniciMapCentricPage;
